import { SourceDataVerificationPermission } from '@/api/dtos';
import ConfigurationForm from '@/components/data/configuration/ConfigurationForm';
import Breadcrumbs from '@/components/layouts/Breadcrumbs';
import PageTitle, { TITLE_SEPARATOR } from '@/components/layouts/PageTitle';
import TrialContext from '@/contexts/data/TrialContext';
import useConfigurationById from '@/hooks/data/useConfigurationById';
import useMetadata from '@/hooks/data/useMetadata';
import useTrialConfigurationByTrialCode from '@/hooks/data/useTrialConfigurationByTrialCode';
import usePermissions from '@/hooks/utility/usePermissions';
import { faDatabase, faGlobeOceania } from '@fortawesome/pro-duotone-svg-icons';
import { Grid, Paper, styled, Typography } from '@mui/material';
import { FC, useContext, useEffect, useMemo, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';

const EditConfiguration: FC = () => {
    const { trialCode } = useParams();
    
    const { data: trial } = useContext(TrialContext);
    const { data: metadata, loading: metadataLoading, validating: metadataUpdating } = useMetadata(trialCode!, { revalidateOnFocus: false });
    const { data: configuration, loading: configurationLoading, validating: configurationUpdating, mutate: configurationMutate } = useTrialConfigurationByTrialCode(!!metadata ? trialCode : undefined, { revalidateOnFocus: false });

    const [ready, setReady] = useState(false);

    useEffect(() => {
        const timeoutId = setTimeout(() => setReady(true), 250);

        return () => {
            clearTimeout(timeoutId);
        }
    }, [])
    
    const breadcrumbs = useMemo(() => {
        return [
            {
                label: 'Configurations',
                link: `/${trialCode}/configurations`,
                icon: faDatabase
            },
            {
                label: 'Trial Configuration',
                icon: faGlobeOceania
            }
        ];
    }, [trialCode])
    
    const loadingData = !ready || metadataLoading || configurationLoading;
    const updating = !loadingData && (metadataUpdating || configurationUpdating);

    const { 
        data: [
            hasEditConfiguration
        ] = [], 
        loading: permissionsLoading, 
        validating: permissionsValidating
    } = usePermissions(
        [
            SourceDataVerificationPermission.EditConfiguration
        ], 
        loadingData,
        trial?.id,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        {
            revalidateOnFocus: false
        }
    );

    const loading = loadingData || permissionsLoading || permissionsValidating;

    if (!hasEditConfiguration && !permissionsLoading && !permissionsValidating) {
        return (
            <Navigate to='/error/403' />
        )
    }

    return (
        <>
            <PageTitle title={`Trial Configuration${TITLE_SEPARATOR}${trial?.name}`} />
            <Breadcrumbs 
                breadcrumbs={breadcrumbs}
            />
            <Typography variant="h1" gutterBottom>
                Trial Configuration
            </Typography>
            <ConfigurationForm
                isTrialConfiguration
                configuration={ready ? configuration : undefined}
                updateConfiguration={configurationMutate}
                trial={trial}
                metadata={ready ? metadata : undefined}
                loading={loading}
                updating={updating}
            />
        </>
    );
}

export default EditConfiguration;